import { NudgeCard } from "@/components/coach/CoachMemberExtras";
import MoneyPyramidPage from "./pages/MoneyPyramidPage";
import CoachRoom from "./pages/CoachRoom";
import NextLevelPage from "./pages/NextLevelPage";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LayoutProvider } from "@/contexts/LayoutContext";
import RequireAuth from "@/components/auth/RequireAuth";
import Join from "./pages/Join";
import RequireApproved from "@/components/auth/RequireApproved";
import RequireAdmin from "@/components/auth/RequireAdmin";
import ErrorBoundary from "@/components/ErrorBoundary";
import AppLoader from "@/components/AppLoader";
import BackToFloor from "@/components/BackToFloor";
import TrainRedirect from "@/components/nav/TrainRedirect";
import LearnGate from "@/components/nav/LearnGate";
import AnalyticsTracker from "@/components/analytics/AnalyticsTracker";
import { useRouteDarkMode } from "@/hooks/useRouteDarkMode";
import { useModerationGuard } from "@/hooks/useModerationGuard";
// Eager: landing + auth so first paint is instant
import Index from "./pages/Index";
import Auth from "./pages/Auth";

// Lazy: everything else loads on demand (massive initial-bundle reduction)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Home = lazy(() => import("./pages/Home"));
const Mission = lazy(() => import("./pages/Mission"));
const TradeDesk = lazy(() => import("./pages/TradeDesk"));
const WheelTracker = lazy(() => import("./pages/WheelTracker"));
const TopDownCharting = lazy(() => import("./pages/TopDownCharting"));
const DealRoom = lazy(() => import("./pages/DealRoom"));
const DeskProgress = lazy(() => import("./pages/DeskProgress"));
const Simulation = lazy(() => import("./pages/Simulation"));
const Profile = lazy(() => import("./pages/Profile"));
const Assessment = lazy(() => import("./pages/Assessment"));
const AssessmentResults = lazy(() => import("./pages/AssessmentResults"));
const BookChapter = lazy(() => import("./pages/BookChapter"));
const CommunityHall = lazy(() => import("./pages/CommunityHallNew"));
const DailySimulation = lazy(() => import("./pages/DailySimulation"));
const Welcome = lazy(() => import("./pages/Welcome"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const Pricing = lazy(() => import("./pages/Pricing"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const Membership = lazy(() => import("./pages/Membership"));
const MembershipInvite = lazy(() => import("./pages/MembershipInvite"));
const InsightsLibrary = lazy(() => import("./pages/InsightsLibrary"));
const TradeSwellEditor = lazy(() => import("./pages/TradeSwellEditor"));
const TradeSwellLive = lazy(() => import("./pages/TradeSwellLive"));
const TradeSwellScanner = lazy(() => import("./pages/TradeSwellScanner"));
const QuantLab = lazy(() => import("./pages/QuantLab"));
const ConsultingRoom = lazy(() => import("./pages/ConsultingRoom"));

const ValueChainEditor = lazy(() => import("./pages/ValueChainEditor"));
const ValueChainView = lazy(() => import("./pages/ValueChainView"));
const LanguageForge = lazy(() => import("./pages/LanguageForge"));
const Valuation = lazy(() => import("./pages/Valuation"));
const RedeemFoundingCode = lazy(() => import("./pages/RedeemFoundingCode"));
const Apply = lazy(() => import("./pages/Apply"));
const Contact = lazy(() => import("./pages/Contact"));
const Pending = lazy(() => import("./pages/Pending"));
const RealEstateFloor = lazy(() => import("./pages/RealEstateFloor"));
const BusinessFloor = lazy(() => import("./pages/BusinessFloor"));
const SignOut = lazy(() => import("./pages/SignOut"));
const CareerSimulator = lazy(() => import("./pages/CareerSimulator"));
const SkillTree = lazy(() => import("./pages/SkillTree"));
const Academy = lazy(() => import("./pages/Academy"));
const Desk = lazy(() => import("./pages/Desk"));
const AcademyTrack = lazy(() => import("./pages/AcademyTrack"));
const AcademyLesson = lazy(() => import("./pages/AcademyLesson"));
const AcademyThesisFlow = lazy(() => import("./pages/AcademyThesisFlow"));
const LayTheTrainPreview = lazy(() => import("./pages/LayTheTrainPreview"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const KingdomDollarsLedger = lazy(() => import("./pages/KingdomDollarsLedger"));
const SwellWatch = lazy(() => import("./pages/SwellWatch"));
const PsychologyMirror = lazy(() => import("./pages/PsychologyMirror"));
const TheologyMirror = lazy(() => import("./pages/TheologyMirror"));
const Metrics = lazy(() => import("./pages/Metrics"));
const Progress = lazy(() => import("./pages/Progress"));


const queryClient = new QueryClient();

// Warm lazy route chunks after first paint so navigation is instant (no black flash)
const preloadRoutes = () => {
  const idle =
    (window as any).requestIdleCallback ||
    ((cb: () => void) => setTimeout(cb, 400));
  idle(() => {
    import("./pages/Home");
    import("./pages/Dashboard");
    import("./pages/Profile");
    import("./pages/CommunityHallNew");
    import("./pages/Academy");
    import("./pages/Portfolio");
    import("./pages/SkillTree");
    import("./pages/Welcome");
    import("./pages/Apply");
    import("./pages/Pending");
    import("./pages/Pricing");
    import("./pages/AdminPortal");
  });
};

function AppRoutes() {
  useRouteDarkMode();
  useModerationGuard();
  useEffect(() => {
    preloadRoutes();
  }, []);
  return (
    <><NudgeCard />
    <Routes>
      {/* Public — landing & funnel only */}
      <Route path="/" element={<Index />} />
      <Route path="/pricing" element={<Navigate to="/join" replace />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/join" element={<Join />} />
      <Route path="/checkout/return" element={<CheckoutReturn />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/invite" element={<MembershipInvite />} />
      <Route path="/book/:chapter" element={<RequireApproved><BookChapter /></RequireApproved>} />
      <Route path="/signout" element={<SignOut />} />
      <Route path="/logout" element={<SignOut />} />
      <Route path="/apply" element={<Navigate to="/join" replace />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/valuation" element={<Navigate to="/join" replace />} />
      <Route
        path="/preview/lay-the-train"
        element={
          <RequireAdmin>
            <LayTheTrainPreview />
          </RequireAdmin>
        }
      />

      {/* Auth-only (no approval required) */}
      <Route path="/redeem" element={<RequireAuth><RedeemFoundingCode /></RequireAuth>} />
      <Route path="/welcome" element={<RequireApproved><Welcome /></RequireApproved>} />
      <Route path="/coach" element={<RequireApproved><CoachRoom /></RequireApproved>} />
      <Route path="/pending" element={<RequireApproved><Pending /></RequireApproved>} />
      <Route path="/membership" element={<RequireApproved><Membership /></RequireApproved>} />

      {/* Approved-member portal */}
      <Route path="/dashboard" element={<RequireApproved><Home /></RequireApproved>} />
      {/* Loop-stage routes: Mission → Train → Desk → Standing */}
      <Route path="/mission" element={<RequireApproved><Mission /></RequireApproved>} />
      <Route path="/home" element={<RequireApproved><Home /></RequireApproved>} />
      <Route path="/train" element={<RequireApproved><TrainRedirect /></RequireApproved>} />
      <Route path="/standing" element={<Navigate to="/forge" replace />} />
      <Route path="/sim/desk/progress" element={<RequireApproved><DeskProgress /></RequireApproved>} />
      <Route path="/sim/deals" element={<RequireApproved><DealRoom /></RequireApproved>} />
      <Route path="/sim/desk" element={<RequireApproved><TradeDesk /></RequireApproved>} />
      <Route path="/sim/wheel" element={<RequireApproved><WheelTracker /></RequireApproved>} />
      <Route path="/sim/charting" element={<RequireApproved><TopDownCharting /></RequireApproved>} />
      <Route path="/sim/:simKey" element={<RequireApproved><Simulation /></RequireApproved>} />
      <Route path="/real-estate-floor" element={<RequireApproved><RealEstateFloor /></RequireApproved>} />
      <Route path="/business-floor" element={<RequireApproved><BusinessFloor /></RequireApproved>} />
      <Route path="/forge" element={<RequireApproved><Dashboard /></RequireApproved>} />
      <Route path="/profile" element={<RequireApproved><Profile /></RequireApproved>} />
      <Route path="/assessment" element={<RequireApproved><Assessment /></RequireApproved>} />
      <Route path="/assessment/results" element={<RequireApproved><AssessmentResults /></RequireApproved>} />
      <Route path="/community" element={<RequireApproved><CommunityHall /></RequireApproved>} />
      <Route path="/insights-library" element={<RequireApproved><InsightsLibrary /></RequireApproved>} />
      <Route path="/trade-swell-editor" element={<RequireAdmin><TradeSwellEditor /></RequireAdmin>} />
      <Route path="/trade-swell-live" element={<RequireApproved><TradeSwellLive /></RequireApproved>} />
      <Route path="/trade-swell-scanner" element={<RequireApproved><TradeSwellScanner /></RequireApproved>} />
      <Route path="/quant-lab" element={<RequireApproved><QuantLab /></RequireApproved>} />
      <Route path="/quant-lab/:module" element={<RequireApproved><QuantLab /></RequireApproved>} />
      <Route path="/swell-watch" element={<RequireApproved><SwellWatch /></RequireApproved>} />
      <Route path="/intern" element={<RequireApproved><Navigate to="/academy" replace /></RequireApproved>} />
      <Route path="/intern/:sectorSlug" element={<RequireApproved><Navigate to="/academy" replace /></RequireApproved>} />
      <Route path="/intern/:sectorSlug/:industrySlug" element={<RequireApproved><Navigate to="/academy" replace /></RequireApproved>} />
      <Route path="/career-simulator" element={<RequireApproved><CareerSimulator /></RequireApproved>} />
      <Route path="/skill-tree" element={<RequireApproved><SkillTree /></RequireApproved>} />
      <Route path="/armory" element={<Navigate to="/dashboard" replace />} />
      <Route path="/world" element={<Navigate to="/dashboard" replace />} />
      <Route path="/world-3d" element={<Navigate to="/dashboard" replace />} />
      <Route path="/command" element={<Navigate to="/dashboard" replace />} />
      <Route path="/academy" element={<RequireApproved><Academy /></RequireApproved>} />
      <Route path="/learn" element={<RequireApproved><LearnGate /></RequireApproved>} />
      <Route path="/desk" element={<RequireApproved><Desk /></RequireApproved>} />
      <Route path="/learn/:trackId" element={<RequireApproved><LearnGate /></RequireApproved>} />
      <Route path="/learn/:trackId/:moduleId" element={<RequireApproved><LearnGate /></RequireApproved>} />
      <Route path="/psychology/mirror" element={<RequireApproved><PsychologyMirror /></RequireApproved>} />
      <Route path="/theology/mirror" element={<RequireApproved><TheologyMirror /></RequireApproved>} />
      <Route path="/academy/thesis-flow" element={<RequireApproved><AcademyThesisFlow /></RequireApproved>} />
      <Route path="/academy/lesson/:lessonId" element={<RequireApproved><AcademyLesson /></RequireApproved>} />
      <Route path="/academy/:trackSlug" element={<RequireApproved><AcademyTrack /></RequireApproved>} />
      <Route path="/portfolio" element={<RequireApproved><Portfolio /></RequireApproved>} />
      <Route path="/kingdom-dollars" element={<RequireApproved><KingdomDollarsLedger /></RequireApproved>} />
      <Route path="/metrics" element={<RequireApproved><Metrics /></RequireApproved>} />
      <Route path="/simulation" element={<RequireApproved><DailySimulation /></RequireApproved>} />
      <Route path="/progress" element={<Navigate to="/metrics" replace />} />
      <Route path="/money" element={<RequireApproved><MoneyPyramidPage /></RequireApproved>} />
      <Route path="/next-level" element={<RequireApproved><NextLevelPage /></RequireApproved>} />

      <Route path="/value-chain-editor" element={<RequireApproved><ValueChainEditor /></RequireApproved>} />
      <Route path="/value-chain/:id" element={<RequireApproved><ValueChainView /></RequireApproved>} />
      <Route path="/language" element={<RequireApproved><LanguageForge /></RequireApproved>} />

      {/* Admin */}
      <Route path="/admin" element={<RequireAdmin><AdminPortal /></RequireAdmin>} />
      
      <Route path="/admin/consulting-room" element={<RequireAdmin><ConsultingRoom /></RequireAdmin>} />
      <Route path="/admin/invites" element={<Navigate to="/admin?section=invites" replace />} />
      <Route path="/admin/vision" element={<Navigate to="/admin?section=vision" replace />} />
      <Route path="/admin/treasury" element={<Navigate to="/admin?section=treasury" replace />} />
      <Route path="/admin/perks" element={<Navigate to="/admin?section=perks" replace />} />
      <Route path="/admin/kd-economy" element={<Navigate to="/admin?section=kd-economy" replace />} />
      <Route path="/admin/decorum-consulting" element={<Navigate to="/admin?section=consulting" replace />} />
      <Route path="/admin/predicted-outcomes" element={<Navigate to="/admin?section=predicted" replace />} />
      <Route path="/kingdom-vision" element={<Navigate to="/admin?section=vision" replace />} />
      <Route path="/banner-men" element={<Navigate to="/admin?section=banner-men" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LayoutProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ErrorBoundary>
              <Suspense fallback={<AppLoader />}>
                <AnalyticsTracker />
                <AppRoutes />
                <BackToFloor />
                <div className="md:hidden h-14" aria-hidden="true" />
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </LayoutProvider>
  </QueryClientProvider>
);

export default App;
